"use client";

import { useEffect, useRef, useState } from "react";

type Member = {
  img: string;
  name: string;
  role: string;
  bio: string;
};

const MEMBERS: Member[] = [
  {
    img: "/assets/images/members/Frank.JPG",
    name: "Frank Boland",
    role: "President",
    bio:
      "Frank Boland is a finance and investment-focused executive with experience spanning aviation, aerospace services, and Asian capital markets.\n\nHe brings a background in equity research, financial modeling, and corporate analysis with UBS Warburg, alongside experience structuring aviation businesses, negotiating lease financing, and executing investment-backed growth strategies across Asia-Pacific markets.\n\nFrank also has direct experience in investment compliance, regulatory structuring, and lease financing for mining concessions in Myanmar and Thailand, operating within complex cross-border approval frameworks. A Mandarin speaker with a Global MBA, he combines investment banking expertise with international business development and strategic finance leadership.",
  },
  {
    img: "/assets/images/members/Kornphong.JPG",
    name: "Kornphong Jaobae",
    role: "Managing Director",
    bio:
      "Kornphong Jaobae is Managing Director of GFS Services with over 15 years of executive leadership experience across aviation, technical services, manufacturing, and mining operations in Thailand.\n\nHe has managed end-to-end mining operations with a focus on regulatory compliance, operational efficiency, and large-scale facility expansion. His background includes government and military contract negotiation, Board of Investment (BOI Thailand) applications, P&L oversight, and strategic growth execution.\n\nKornphong brings strong organizational leadership and operational discipline, ensuring regulatory alignment and scalable infrastructure to support long-term business expansion.",
  },
  {
    img: "/assets/images/members/Steve.JPG",
    name: "Steve Rynecki",
    role: "Sustainability Advisor",
    bio:
      "Steve Rynecki serves as a sustainability advisor to GFS, bringing over 20 years of experience with the U.S. State Department and more than a decade of work in Southeast Asia focused on supply chain and sourcing initiatives. Based in Bangkok, he specializes in critical minerals and rare earth elements supplied to international markets.\n\nHis expertise centers on responsible sourcing, supply chain traceability, and alignment with global sustainability, compliance, and governance standards. Drawing from both public and private sector experience, he advises on risk mitigation, transparency, and long-term supply reliability.\n\nSteve provides a policy-informed and operationally practical perspective on cross-border supply chains, supporting GFS in meeting regulatory requirements and the expectations of global partners and buyers.",
  },
  {
    img: "/assets/images/members/Charles.JPG",
    name: "Charles Ye",
    role: "Senior Geologist",
    bio:
      "Charles Ye is a senior geologist with over 40 years of experience in mineral exploration, mining operations, and petroleum geology. He holds a B.Sc. in Geology and has led exploration and development programs across commodities including antimony systems, gold, copper, coal, nickel, and chromite.\n\nHe has held senior technical and project leadership roles within both government and international mining organizations, guiding projects from early-stage exploration through feasibility and production. His experience includes large-scale diamond drilling oversight, mine development planning, and international project management.\n\nCharles previously served in a long-standing role with Myanmar's Department of Geological Survey & Mineral Exploration under the Ministry of Mines. He is recognized for his disciplined execution, strong field judgment, and steady leadership in complex geological environments.",
  },
  {
    img: "/assets/images/members/Thaw.JPG",
    name: "Min Kun Htaw",
    role: "Operations Director",
    bio:
      "Min Kun Htaw is Operations Director of GFS Services, with extensive experience managing resource-sector operations across Myanmar.\n\nHe has overseen oil and gas concessions as well as mining projects focused on antimony and rare earth minerals. His expertise includes government liaison and permitting, regulatory compliance, remote field logistics, supply chain coordination, and supervision of assay, sampling, and in-field testing programs.\n\nMin Khun brings practical, field-driven operational leadership, ensuring disciplined execution across exploration and production activities in challenging environments.",
  },
  {
    img: "/assets/images/members/Gary.JPG",
    name: "Gary Villiard",
    role: "Technical Advisor",
    bio:
      "Gary Villiard is GFS's technical advisor with more than four decades of leadership experience across mining, commercial aviation, helicopter operations, MRO, and gas turbine systems.\n\nHe has served as CEO, President, and Owner, providing high-level technical, operational, and regulatory advisory support worldwide. His expertise includes FAA Part 121, 135, and 145 compliance, STC engineering, fleet modernization, safety management systems, and large-scale maintenance operations.\n\nGary also brings hands-on operational experience supporting diamond mining and lumber processing operations in Brazil, including equipment management and remote logistics coordination. He is recognized for establishing Thailand's only certified helicopter and gas turbine engine overhaul facility and advising organizations across Southeast Asia, the United States, Eastern Europe, and complex operating environments.",
  }
];

export default function TeamShowcaseGrid() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const els = container.querySelectorAll(".reveal");
    if (!els) return;

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleKeyToggle = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpenIndex((cur) => (cur === idx ? null : idx));
    }
  };

  return (
    <section
      ref={ref}
      className="section-navy-mid"
      style={{ paddingTop: 80, paddingBottom: 120 }}
      aria-label="Team showcase"
    >
      <div className="px-6 lg:px-12" style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="text-center mb-12 reveal reveal-delay-1">
          <p className="type-caption" style={{ color: "var(--color-red)", marginBottom: 8 }}>
            MEET OUR TEAM
          </p>
          <h2
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              fontWeight: 400,
              color: "var(--color-white)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
          >
            The People Driving Execution
          </h2>
        </div>

        {/* GRID */}
        <div
          className="reveal reveal-delay-2 team-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 320px)",
            gap: 50,
            justifyContent: "center",
            alignItems: "start",
          }}
        >
          {MEMBERS.map((m, i) => {
            const isOpen = openIndex === i;

            return (
              <div
                key={m.name}
                className="team-card"
                style={{
                  position: "relative",
                  width: 320,
                  height: 400,
                  borderRadius: 18,
                  overflow: "hidden",
                  boxShadow: "0 8px 30px rgba(2,6,23,0.35)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  cursor: "pointer",
                  transition: "transform 240ms ease, box-shadow 240ms ease",
                  backgroundColor: "rgba(2,6,23,0.02)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(-6px)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "0 18px 44px rgba(2,6,23,0.45)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 30px rgba(2,6,23,0.35)";
                }}
                onClick={() => setOpenIndex((cur) => (cur === i ? null : i))}
                onKeyDown={(e) => handleKeyToggle(e as any, i)}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-label={`${m.name} — ${m.role}. Click or press enter to read bio.`}
              >
                {/* portrait background */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0.0) 0%, rgba(2,6,23,0.62) 62%), url("${m.img}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    zIndex: 1,
                    transformOrigin: "center",
                    transition: "transform 700ms cubic-bezier(.22,.9,.35,1)",
                  }}
                />

                {/* bottom text */}
                <div style={{ position: "relative", zIndex: 3, padding: "22px 20px 26px", textAlign: "left" }}>
                  <div style={{ color: "rgba(255,255,255,0.95)", fontFamily: "var(--font-serif)", fontSize: "1.5rem", marginBottom: 6 }}>
                    {m.name}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.65)", fontSize: ".9rem", fontWeight: 600, letterSpacing: ".02em" }}>
                    {m.role}
                  </div>
                </div>

                {/* full-card bio overlay (slides up from bottom) */}
                <div
                  className="bio-overlay"
                  onClick={() => setOpenIndex((cur) => (cur === i ? null : i))}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    top: 0,
                    zIndex: 8,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    pointerEvents: isOpen ? "auto" : "none",
                  }}
                >
                  <div
                    role="region"
                    aria-hidden={!isOpen}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      padding: 0,
                    }}
                  >
                    <div
                      className="bio-panel"
                      onClick={(e) => e.stopPropagation()} // prevent closing when tapping inside the panel
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "var(--color-navy)",
                        color: "rgba(255,255,255,0.96)",
                        transform: isOpen ? "translateY(0%)" : "translateY(100%)",
                        transition: "transform 420ms cubic-bezier(.22,.9,.35,1), opacity 320ms ease",
                        opacity: isOpen ? 1 : 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        padding: "28px",
                        boxSizing: "border-box",
                        overflow: "auto",
                        // keep panel readable on small screens
                        maxHeight: "80vh",
                      }}
                    >
                      <div style={{ marginTop: 8 }}>
                        <div style={{ color: "var(--color-red)", fontFamily: "var(--font-serif)", fontSize: "1.25rem", marginBottom: 8 }}>{m.name}</div>
                        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: ".95rem", marginBottom: 14 }}>{m.role}</div>

                        {/* Render bio as paragraphs with spacing and support single \n */}
                        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: ".98rem", lineHeight: 1.6 }}>
                          {m.bio.split("\n\n").map((paragraph, idx) => (
                            <p
                              key={idx}
                              style={{
                                margin: 0,
                                marginBottom: 16,
                                whiteSpace: "pre-line", // allows single \n to render as line breaks
                                lineHeight: 1.7,
                              }}
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginTop: "auto", width: "100%", textAlign: "center", paddingTop: 22 }}>
                        <div style={{ width: 60, height: 4, background: "rgba(255,255,255,0.12)", margin: "0 auto", borderRadius: 3 }} />
                        <div style={{ fontSize: ".82rem", color: "rgba(255,255,255,0.6)", marginTop: 8 }}>Tap or click anywhere to close</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        /* hover/focus should reveal overlay on desktop */
        .team-card:hover .bio-overlay .bio-panel,
        .team-card:focus .bio-overlay .bio-panel,
        .team-card:focus-within .bio-overlay .bio-panel {
          transform: translateY(0%) !important;
          opacity: 1 !important;
          pointer-events: auto !important;
        }

        /* reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .team-card, .bio-overlay, .bio-panel {
            transition: none !important;
          }
        }

        /* Responsive grid: 3 cols desktop, 2 cols tablet, 1 col mobile */
        @media (max-width: 1100px) {
          .team-grid {
            grid-template-columns: repeat(2, 320px) !important;
            justify-content: center !important;
          }
          .team-card { width: 320px !important; }
        }
        @media (max-width: 700px) {
          .team-grid {
            grid-template-columns: repeat(1, minmax(260px, 1fr)) !important;
            justify-content: center !important;
          }
          .team-card { width: 100% !important; max-width: 420px; height: 420px; }
        }

        /* make bio-panel content scrollable on small devices */
        .bio-panel { -webkit-overflow-scrolling: touch; }
      `}</style>
    </section>
  );
}